import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { UserForm } from '../form'
import { useCreateUser, useUpdateUser } from '../hooks'
import { createUserSchema, updateUserSchema, type UserFormData } from '../schema'
import type { User } from '@/types/entities'

interface UserFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    user: User | null
}

export function UserFormDialog({ open, onOpenChange, user }: UserFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!user
    const createUser = useCreateUser()
    const updateUser = useUpdateUser()
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
    const [originalAvatarUrl, setOriginalAvatarUrl] = useState<string | null>(null)

    const form = useForm<UserFormData>({
        resolver: zodResolver(isEditing ? updateUserSchema : createUserSchema) as any,
        mode: 'onChange',
        defaultValues: {
            name: '',
            email: '',
            password: '',
            roles: [],
            teams: [],
            isActive: true,
            mfaRequired: false,
            mfaMethods: [],
            language: 'en-US',
            timezone: 'UTC',
            location: null,
            phoneCode: '+44',
            phoneNumber: null,
            avatarUrl: '',
            avatarColor: '',
        },
    })

    // Reset form when dialog opens/closes or user changes
    useEffect(() => {
        if (open) {
            if (user) {
                // Track the original avatar URL for cleanup purposes
                setOriginalAvatarUrl(user.avatarUrl || null)
                form.reset({
                    name: user.name,
                    email: user.email,
                    password: '', // Don't populate password for existing users
                    roles: user.roles?.map(role => role.id) || [],
                    teams: user.teams?.map(team => team.id) || [],
                    primaryTeamId: user.teams?.find(team => team.isPrimary)?.id || null,
                    isActive: user.isActive,
                    standby: user.standby ?? false,
                    mfaMethods: [
                        ...(user.mfa?.totpEnabled ? ['totp' as const] : []),
                        ...(user.mfa?.webauthnEnabled ? ['webauthn' as const] : [])
                    ],
                    mfaRequired: (user.mfa?.totpEnabled || user.mfa?.webauthnEnabled) || false,
                    language: user.language || 'en-US',
                    timezone: user.timezone || 'UTC',
                    location: user.location || null,
                    phoneCode: (user as any).phoneCode ?? '+44',
                    phoneNumber: (user as any).phoneNumber || null,
                    avatarUrl: user.avatarUrl || '',
                    avatarColor: user.avatarColor || '',
                })
            } else {
                // Creating new user - ensure all fields are blank
                setOriginalAvatarUrl(null)
                form.reset({
                    name: '',
                    email: '',
                    password: '',
                    roles: [],
                    teams: [],
                    primaryTeamId: null,
                    isActive: true,
                    standby: false,
                    mfaRequired: false,
                    mfaMethods: [],
                    language: 'en-US',
                    timezone: 'UTC',
                    location: null,
                    phoneCode: '+44',
                    phoneNumber: null,
                    avatarUrl: '',
                    avatarColor: '',
                }, {
                    keepDefaultValues: false,
                    keepValues: false,
                    keepDirty: false,
                    keepErrors: false,
                    keepIsSubmitted: false,
                    keepTouched: false,
                    keepIsValid: false,
                    keepSubmitCount: false,
                })
            }
        } else {
            // When dialog closes, reset form to default values
            form.reset({
                name: '',
                email: '',
                password: '',
                roles: [],
                teams: [],
                primaryTeamId: null,
                isActive: true,
                standby: false,
                mfaRequired: false,
                mfaMethods: [],
                language: 'en-US',
                timezone: 'UTC',
                location: null,
                phoneCode: '+44',
                phoneNumber: null,
                avatarUrl: '',
                avatarColor: '',
            })
        }
    }, [open, user, form])


    // Handle form submission with proper validation
    const handleSubmit = async (data: UserFormData) => {
        try {
            // Validate teams and primary team
            if (!data.teams || data.teams.length === 0) {
                form.setError('teams', {
                    type: 'manual',
                    message: 'At least one team is required'
                })
                return
            }
            
            if (!data.primaryTeamId) {
                form.setError('primaryTeamId', {
                    type: 'manual',
                    message: 'A primary team must be selected'
                })
                return
            }
            
            if (!data.teams.includes(data.primaryTeamId)) {
                form.setError('primaryTeamId', {
                    type: 'manual',
                    message: 'Primary team must be one of the selected teams'
                })
                return
            }
            
            if (isEditing && user) {
                const updateData = { ...data, originalAvatarUrl }
                if (!updateData.password) {
                    delete updateData.password
                }
                // Ensure mfaMethods is always included if mfaRequired is true
                if (updateData.mfaRequired && !updateData.mfaMethods) {
                    updateData.mfaMethods = []
                }
                // Ensure primaryTeamId is included in the update
                const updatePayload = {
                    ...updateData,
                    teams: updateData.teams || [],
                    primaryTeamId: updateData.primaryTeamId || null
                }
                console.log('Updating user with data:', updatePayload)
                await updateUser.mutateAsync({
                    id: user.id,
                    data: updatePayload as any,
                })
            } else {
                // Ensure primaryTeamId is included in the create
                const createPayload = {
                    ...data,
                    teams: data.teams || [],
                    primaryTeamId: data.primaryTeamId || null
                }
                console.log('Creating user with data:', { 
                    name: createPayload.name,
                    email: createPayload.email,
                    password: '[REDACTED]',
                    roles: createPayload.roles,
                    teams: createPayload.teams,
                    primaryTeamId: createPayload.primaryTeamId,
                    isActive: createPayload.isActive
                })
                await createUser.mutateAsync(createPayload as any)
            }
            onOpenChange(false)
        } catch (error) {
            console.error('Form submit error:', error)
        }
    }

    const isLoading = createUser.isPending || updateUser.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col p-6 gap-4">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle>
                        {isEditing ? t('userForm.editUser') : t('userForm.createUser')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('userForm.updateUserInfo')
                            : t('userForm.createNewUserDescription')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit as any)} className="flex flex-col flex-1 min-h-0">
                        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
                            <UserForm
                                form={form as any}
                                isEditing={isEditing}
                                user={user}
                                isUploadingAvatar={isUploadingAvatar}
                                setIsUploadingAvatar={setIsUploadingAvatar}
                            />
                        </div>
                        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t mt-4">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {t('userForm.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading || isUploadingAvatar}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {isLoading ? (isEditing ? t('userForm.updating') : t('userForm.creating')) : isUploadingAvatar ? t('userForm.uploading') : isEditing ? t('userForm.update') : t('userForm.create')}
                            </button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

